import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { FaDollarSign, FaPiggyBank, FaChartBar, FaCloud, FaExclamationTriangle, FaCogs } from 'react-icons/fa';
import { CONST_SITE_LABEL, PLATFORM, STORAGE_KEYS, formatValuesInK, setScopedItem, toWholeString, buildExportFilename, exportCSV, ADD_NEW_ACCOUNT } from '../components/common/Helper';
import DashboardService from '../services/DashboardService';
import { useCallback, useContext, useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../state/AuthContext';
import DebugService from '../services/DebugService';
import { DateContext } from '../state/DateContext';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import BillingApiService from '../services/BillingApiService';
import K8sApiService from '../services/K8sApiService';
import { CONST_ROUTE_PATH } from '../route/RouteConstants';
import Chart from 'chart.js/auto';
import Card from '../components/common/Card';
import Table from '../components/common/Table.jsx';
import { BadgeSkeleton, KpiSkeleton, SvcGridSkeleton, ChartGridSkeleton, TableSkeleton, RecsPanelSkeleton } from '../components/optimization/Skeletons';

dayjs.extend(relativeTime);

/* ── Module-level constants ─────────────────────────────────── */
const MONTHS_PER_YEAR    = 12;
const TOP_SERVICES_LIMIT = 6;
const TOP_CLUSTERS_LIMIT = 3;
const CHART_Y_HEADROOM   = 1.25;
const CHART_Y_MIN        = 0.1;

/* ── Anomaly detection — mirrors backend ANOMALY_COST_MAPPING ── */
const ANOMALY_BASELINE_WINDOW = 14;   // rolling window of historical days
const ANOMALY_MIN_BASELINE    = 7;    // need ≥7 data points before detecting
const ANOMALY_COST_FLOOR      = 0.50; // ignore avg daily cost below $0.50
const ANOMALY_MIN_DELTA       = 0.30; // ignore absolute increase below $0.30
const ANOMALY_Z_LOW           = 1.5;
const ANOMALY_Z_MED           = 2.0;
const ANOMALY_Z_HIGH          = 3.0;
const ANOMALY_LOOKBACK_DAYS   = 30;   // only flag anomalies in the last 30 days

const getSeverityFromZ = (z) => {
  if (z >= ANOMALY_Z_HIGH) return 'High';
  if (z >= ANOMALY_Z_MED)  return 'Med';
  if (z >= ANOMALY_Z_LOW)  return 'Low';
  return null;
};

const ANOMALY_DOT_COLOR = { High: '#e53935', Med: '#fb8c00', Low: '#f9a825' };

const CHART_COLORS = {
  cost:     '#f57c00',
  savings:  '#2196f3',
  unused:   '#2ea66f',
  node:     '#1e88e5',
  storage:  '#43a047',
  lb:       '#f57c00',
  idle:     '#ef4444',
  anomaly:  '#ef4444',
  line:     '#2196f3',
  lineFill: 'rgba(33,150,243,0.14)',
  grid:     '#eef1f7',
  sevHigh:  '#e53935',
  sevMed:   '#fb8c00',
  sevLow:   '#43a047',
  sevUnk:   '#999',
};

const EXCLUDED_FROM_TOTAL_KEYS = ['FORECAST_COST', 'ROI'];

const UNUSED_SERVICE_KEYS  = new Set(['EBS', 'ELASTIC_IP', 'EBS_SNAPSHOT', 'RDS_SNAPSHOT', 'MANAGED_DISK', 'PUBLIC_IP', 'EC2', 'RDS', 'ASG']);
const UNUSED_SHORT_LABELS  = { EBS: 'EBS', ELASTIC_IP: 'EIP', EBS_SNAPSHOT: 'EBSS', RDS_SNAPSHOT: 'RDSS', MANAGED_DISK: 'Disk', PUBLIC_IP: 'PIP', EC2: 'EC2', RDS: 'RDS', ASG: 'ASG' };

const extractResourceName = (row) => {
  const arn = row.functionArn || row.instanceArn || row.volumeArn || row.autoScalingGroupArn || row.autoScalingGroupName || '';
  if (!arn) return row.resourceName ?? '—';
  const afterFunction = arn.split(':function:')[1];
  if (afterFunction) return afterFunction.split(':')[0];
  const afterSlash = arn.split('/').pop();
  if (afterSlash && afterSlash !== arn) return afterSlash;
  const parts = arn.split(':');
  return parts[parts.length - 2] || parts[parts.length - 1] || arn;
};

/* Finding → colored badge palette */
const FINDING_STYLES = {
  optimized:        { bg: '#e6f4ea', fg: '#1e7e34', dot: '#43a047' },
  idle:             { bg: '#fff4e5', fg: '#b26a00', dot: '#fb8c00' },
  underprovisioned: { bg: '#fdecea', fg: '#c62828', dot: '#e53935' },
  overprovisioned:  { bg: '#fdecea', fg: '#c62828', dot: '#e53935' },
  notoptimized:     { bg: '#fdecea', fg: '#c62828', dot: '#e53935' },
  default:          { bg: '#eef1f7', fg: '#5b6472', dot: '#999' },
};

const FindingBadge = ({ finding }) => {
  if (!finding) return <span style={{ color: 'var(--muted, #999)' }}>—</span>;
  const s = FINDING_STYLES[String(finding).toLowerCase()] || FINDING_STYLES.default;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: s.bg, color: s.fg,
      fontSize: 12, fontWeight: 600, lineHeight: 1.4,
      padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {finding}
    </span>
  );
};

/* Human-readable remark/recommendation for a row */
const extractRecommendation = (row) => {
  if (row.findingDescription) return row.findingDescription;
  const codes = Array.isArray(row.findingReasonCodes) ? row.findingReasonCodes.filter(Boolean) : [];
  if (codes.length > 0) return codes.join(', ');
  const finding = String(row.finding ?? '').toLowerCase();
  if (finding === 'optimized') return 'Already optimized — no action needed.';
  if (finding === 'idle') return 'Idle resource — consider deleting to save cost.';
  if (finding === 'underprovisioned') return 'Under-provisioned — consider scaling up.';
  if (finding === 'overprovisioned') return 'Over-provisioned — consider scaling down.';
  return finding ? `${row.finding} — review recommended.` : '—';
};

const SavingsCell = ({ value }) => {
  const amount = parseFloat(value ?? 0) || 0;
  const positive = amount > 0;
  return (
    <span style={{
      fontWeight: 700, fontVariantNumeric: 'tabular-nums',
      color: positive ? 'var(--green, #1e7e34)' : 'var(--muted, #8a929e)',
    }}>
      ${amount.toFixed(2)}
    </span>
  );
};

const OPTIMIZER_COLUMNS = [
  { key: 'resourceType', label: 'Resource Type', render: (row) => (
      <span style={{ fontWeight: 600, color: 'var(--text, #222)' }}>{row.resourceType ?? '—'}</span>
    ) },
  { key: 'resourceName', label: 'Resource Name', render: (row) => extractResourceName(row) },
  { key: 'accountId',    label: 'Account',       render: (row) => (
      <span style={{ color: 'var(--muted, #5b6472)', fontVariantNumeric: 'tabular-nums' }}>{row.accountId ?? '—'}</span>
    ) },
  { key: 'region',       label: 'Region',        render: (row) => (
      <span style={{
        background: '#eef4ff', color: '#2b5bb5', fontSize: 12, fontWeight: 600,
        padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap',
      }}>{row.region ?? '—'}</span>
    ) },
  { key: 'finding',      label: 'Finding',        render: (row) => <FindingBadge finding={row.finding} /> },
  { key: 'recommendation', label: 'Recommendation', minWidth: 260, render: (row) => (
      <span style={{ color: 'var(--text, #444)', fontSize: 13 }}>{extractRecommendation(row)}</span>
    ) },
  { key: 'savingsUSDPerMonth', label: 'Saving / mo', className: 'num', render: (row) => <SavingsCell value={row.savingsUSDPerMonth} /> },
];

const AWS_SVC_CARDS = [
  { key: 'EC2',          label: 'EC2 Recommendations',  costKey: 'currentCost'    },
  { key: 'EBS',          label: 'EBS Recommendations',  costKey: 'currentCost'    },
  { key: 'RDS',          label: 'RDS Recommendations',  costKey: 'currentCost'    },
  { key: 'ELASTIC_IP',   label: 'Elastic IP Savings',   costKey: 'currentCost'    },
  { key: 'EBS_SNAPSHOT', label: 'EBS Snapshot Savings', costKey: 'totalCostValue' },
];

const AZURE_SVC_CARDS = [
  { key: 'VM',           label: 'VM Recommendations',        costKey: 'currentCost' },
  { key: 'MANAGED_DISK', label: 'Managed Disk',              costKey: 'currentCost' },
  { key: 'PUBLIC_IP',    label: 'Public IP Recommendations', costKey: 'currentCost' },
];

/* ── K8s cost allocation doughnut ────────────────────────────── */
const K8sCostDonutChart = ({ nodeCost, storageCost, lbCost, idleCost }) => {
  const canvasRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    const vals = [nodeCost, storageCost, lbCost, idleCost];
    if (!vals.some(v => v > 0) || !canvasRef.current) return;
    if (instanceRef.current) { instanceRef.current.destroy(); instanceRef.current = null; }
    instanceRef.current = new Chart(canvasRef.current.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: ['Node', 'Storage', 'Load Balancer', 'Idle'],
        datasets: [{ data: vals, backgroundColor: [CHART_COLORS.node, CHART_COLORS.storage, CHART_COLORS.lb, CHART_COLORS.idle], borderWidth: 2, borderColor: '#fff' }],
      },
      options: {
        maintainAspectRatio: false,
        cutout: '68%',
        plugins: { legend: { display: false }, tooltip: { callbacks: { label: c => `$${c.parsed.toFixed(2)}` } } },
      },
    });
    return () => { if (instanceRef.current) { instanceRef.current.destroy(); instanceRef.current = null; } };
  }, [nodeCost, storageCost, lbCost, idleCost]);

  return <canvas ref={canvasRef} />;
};

/* ── Service mini-bar chart ──────────────────────────────────── */
const ServiceMiniBarChart = ({ cost, saving }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    const costK = parseFloat(((cost || 0) / 1000).toFixed(2));
    const savK  = parseFloat(((saving || 0) / 1000).toFixed(2));
    const maxK  = Math.max(parseFloat((Math.max(costK, savK) * CHART_Y_HEADROOM).toFixed(2)), CHART_Y_MIN);
    const ctx = canvasRef.current.getContext('2d');
    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Cost', 'Savings'],
        datasets: [{ data: [costK, savK], backgroundColor: [CHART_COLORS.cost, CHART_COLORS.savings], borderRadius: 4, barPercentage: 0.55 }],
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: c => `$${c.parsed.y.toFixed(2)}K` } },
        },
        scales: {
          y: { beginAtZero: true, max: maxK, ticks: { font: { size: 9 }, callback: v => `$${v}K` }, grid: { color: CHART_COLORS.grid } },
          x: { ticks: { font: { size: 10 } }, grid: { display: false } },
        },
      },
    });
    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [cost, saving]);

  return <canvas ref={canvasRef} />;
};

/* ── Horizontal bar chart card ───────────────────────────────── */
const HorizontalBarChart = ({ labels, fullLabels, data, color, title, subTitle }) => {
  const canvasRef = useRef(null);
  const instanceRef = useRef(null);

  useEffect(() => {
    if (!labels?.length || !data?.length) return;
    if (instanceRef.current) instanceRef.current.destroy();
    const ctx = canvasRef.current.getContext('2d');
    instanceRef.current = new Chart(ctx, {
      type: 'bar',
      data: { labels, datasets: [{ data, backgroundColor: color, borderRadius: 4, barPercentage: 0.6 }] },
      options: {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: {
            title: (items) => fullLabels ? (fullLabels[items[0].dataIndex] ?? items[0].label) : items[0].label,
            label: c => `$${c.parsed.x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          }},
        },
        scales: {
          x: { ticks: { font: { size: 9 }, callback: v => `$${(v / 1000).toFixed(1)}K` }, grid: { color: CHART_COLORS.grid } },
          y: { ticks: { font: { size: 10 } }, grid: { display: false } },
        },
      },
    });
    return () => { if (instanceRef.current) instanceRef.current.destroy(); };
  }, [labels, fullLabels, data, color]);

  return (
    <Card title={title} subtitle={subTitle} className="first">
      <div style={{ height: '220px', position: 'relative', marginTop: '10px' }}>
        <canvas ref={canvasRef} />
      </div>
    </Card>
  );
};

/* ── Daily cost trend line chart ─────────────────────────────── */
const DailyCostTrendChart = ({ labels, data, platformLabel }) => {
  const canvasRef = useRef(null);
  const instanceRef = useRef(null);

  /* Rolling-window z-score detection — same algorithm as backend detectCostAnomalies.
     Only flags anomalies in the last ANOMALY_LOOKBACK_DAYS days of the series. */
  const anomalies = useMemo(() => {
    if (data.length < ANOMALY_MIN_BASELINE + 1) return [];
    const result = [];
    const lookbackStart = Math.max(ANOMALY_MIN_BASELINE, data.length - ANOMALY_LOOKBACK_DAYS);
    for (let i = lookbackStart; i < data.length; i++) {
      // Baseline = up to ANOMALY_BASELINE_WINDOW days before index i
      const windowStart = Math.max(0, i - ANOMALY_BASELINE_WINDOW);
      const baseline = data.slice(windowStart, i);
      if (baseline.length < ANOMALY_MIN_BASELINE) continue;

      const avg = baseline.reduce((a, b) => a + b, 0) / baseline.length;
      if (avg < ANOMALY_COST_FLOOR) continue;

      const sd = Math.sqrt(baseline.reduce((s, v) => s + (v - avg) ** 2, 0) / baseline.length);
      const currentCost = data[i];
      const absoluteDelta = Math.max(0, currentCost - avg);
      if (absoluteDelta < ANOMALY_MIN_DELTA) continue;
      if (sd === 0) continue;

      const z = (currentCost - avg) / sd;
      if (!Number.isFinite(z)) continue;

      const severity = getSeverityFromZ(z);
      if (!severity) continue;

      result.push({ index: i, severity, z });
    }
    return result;
  }, [data]);

  /* Fast index → severity lookup for chart rendering */
  const anomalyMap = useMemo(() => {
    const m = {};
    anomalies.forEach(a => { m[a.index] = a; });
    return m;
  }, [anomalies]);

  useEffect(() => {
    if (!labels?.length || !data?.length) return;
    if (instanceRef.current) instanceRef.current.destroy();
    const ctx = canvasRef.current.getContext('2d');
    instanceRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [{
          data,
          borderColor: CHART_COLORS.line, backgroundColor: CHART_COLORS.lineFill,
          fill: true, tension: 0.3, borderWidth: 2,
          pointRadius: data.map((_, i) => anomalyMap[i] ? 7 : 2),
          pointBackgroundColor: data.map((_, i) =>
            anomalyMap[i] ? (ANOMALY_DOT_COLOR[anomalyMap[i].severity] ?? CHART_COLORS.anomaly) : CHART_COLORS.line
          ),
          pointBorderColor: '#fff', pointBorderWidth: 2,
        }],
      },
      options: {
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: c => {
            const a = anomalyMap[c.dataIndex];
            return `$${c.parsed.y.toFixed(2)}${a ? ` ⚠ ${a.severity} anomaly (z=${a.z.toFixed(1)})` : ''}`;
          }}},
        },
        scales: {
          y: { beginAtZero: false, ticks: { font: { size: 10 }, callback: v => `$${v.toLocaleString()}` }, grid: { color: CHART_COLORS.grid } },
          x: { ticks: { font: { size: 9 }, autoSkip: true, maxRotation: 0 }, grid: { display: false } },
        },
      },
    });
    return () => { if (instanceRef.current) instanceRef.current.destroy(); };
  }, [labels, data, anomalyMap]);

  /* Badge breakdown by severity */
  const sevCounts = useMemo(() => {
    const c = { High: 0, Med: 0, Low: 0 };
    anomalies.forEach(a => { c[a.severity] = (c[a.severity] || 0) + 1; });
    return c;
  }, [anomalies]);

  const badgeParts = ['High', 'Med', 'Low']
    .filter(s => sevCounts[s] > 0)
    .map(s => `${sevCounts[s]} ${s}`);

  return (
    <Card
      title={<>
        Daily Cost Trend
        {anomalies.length > 0 && (
          <span className="anom-badge" style={{ marginLeft: 8 }}>
            ⚠ {anomalies.length} {anomalies.length === 1 ? 'anomaly' : 'anomalies'} · {badgeParts.join(' · ')}
          </span>
        )}
      </>}
      subtitle={`Daily ${platformLabel} spend · last ${labels.length} days · anomaly detection: last ${ANOMALY_LOOKBACK_DAYS} days`}
      className="first"
    >
      <div style={{ height: '260px', position: 'relative', marginTop: '10px' }}>
        <canvas ref={canvasRef} />
      </div>
    </Card>
  );
};

const shortenLabel = (label) => {
  if (!label) return label;
  const expanded = label.replace(/([A-Z])/g, ' $1').trim();
  const words = expanded.split(' ').filter(Boolean);
  const firstLower = words[0].toLowerCase();
  const filtered = (firstLower === 'amazon' || firstLower === 'aws') ? words.slice(1) : words;
  return filtered.map(w => w[0]?.toUpperCase()).join('');
};

const SVC_TAG_CLASS = {
  EC2: 'ec2', EBS: 'ebs', RDS: 'rds', ELASTIC_IP: 'eip',
  EBS_SNAPSHOT: 'snap', RDS_SNAPSHOT: 'snap', ROI: 'roi', EKS: 'eks', COMPUTE_OPTIMIZER: 'co',
  VM: 'vm', MANAGED_DISK: 'disk', PUBLIC_IP: 'pip', POSTGRESQL: 'pg',
};

/* ── Main Dashboard Page ─────────────────────────────────────── */
const Dashboard = () => {
  const [allServicesData, setAllServicesData] = useState({});
  const [totalMonthlyCost, setTotalMonthlyCost] = useState('0');
  const [totalMonthlySaving, setTotalMonthlySaving] = useState('0');
  const [totalMonthlySavingPer, setTotalMonthlySavingPer] = useState('0');
  const [VMMonthlySaving, setVMMonthlySaving] = useState('0');
  const [ManagedDiskMonthlySaving, setManagedDiskMonthlySaving] = useState('0');
  const [PublicIPMonthlySaving, setPublicIPMonthlySaving] = useState('0');

  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const datePillRef = useRef(null);
  const lastFetchParamsRef = useRef(null);
  const [pillMounted, setPillMounted] = useState(false);
  useLayoutEffect(() => { if (datePillRef.current) setPillMounted(true); }, []);
  const { isSubscriptionActive, selectedPlatform, selectedAccount, loadingUserDetails, accountList } = useAuth();
  const { selectedDate, setSelectedDate } = useContext(DateContext);
  const { getScopedItem } = useScopedLocalStorage();
  const navigate = useNavigate();

  const [costExplorerColumnList, setCostExplorerColumnList] = useState([]);
  const [costExplorerData, setCostExplorerData] = useState([]);
  const [costExplorerApiData, setCostExplorerApiData] = useState([]);
  const [advisoryColumnList, setAdvisoryColumnList] = useState([]);
  const [advisoryApiData, setAdvisoryApiData] = useState([]);
  const [allOptimizerColumnList, setAllOptimizerColumnList] = useState([]);
  const [allOptimizerData, setAllOptimizerData] = useState([]);
  const [anomalyColumnList, setAnomalyColumnList] = useState([]);
  const [anomalyData, setAnomalyData] = useState([]);
  const [unusedResourcesData, setUnusedResourcesData] = useState([]);
  const [isOptimizerLoading, setIsOptimizerLoading] = useState(true);
  const [k8sData, setK8sData] = useState(null);
  const [isK8sLoading, setIsK8sLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [lastK8sRefreshed, setLastK8sRefreshed] = useState(null);

  const optimizerMonthlyTotal = useMemo(
    () => allOptimizerData.reduce((sum, item) => {
      const val = parseFloat((item.estimatedMonthlySavings ?? item.savingsUSDPerMonth ?? '').toString().replace('$', ''));
      return sum + (isNaN(val) ? 0 : val);
    }, 0),
    [allOptimizerData]
  );

  const [dataFilters, setDataFilters] = useState({
    startMonthDate: dayjs().subtract(30, 'days'),
    endMonthDate: dayjs(),
    granularity: 'DAILY',
    dimension: selectedPlatform === PLATFORM.AWS ? 'SERVICE' : 'SERVICE_NAME',
    filter: '',
  });

  /* ── Derived chart data ────────────────────────────────────── */
  const savingsByResourceType = useMemo(() => {
    let unused = 0, idle = 0;

    unusedResourcesData.forEach(item => {
      const state = (item.finding ?? '').toUpperCase();
      const val = parseFloat((item.estimatedMonthlySavings ?? item.savingsUSDPerMonth ?? 0).toString().replace('$', ''));
      if (!isNaN(val) && val > 0) {
        if (state.includes('IDLE')) idle += val;
        else unused += val;
      }
    });

    if (unused > 0 || idle > 0) {
      const entries = [['Unused', unused], ['Idle', idle]].filter(([, v]) => v > 0);
      return { labels: entries.map(([k]) => k), data: entries.map(([, v]) => parseFloat(v.toFixed(2))) };
    }

    // Fallback: use allServicesData — aggregate all unused-resource services into Unused bucket
    const fallbackTotal = Object.entries(allServicesData)
      .filter(([key, v]) => UNUSED_SERVICE_KEYS.has(key) && (v?.savingCost ?? 0) > 0)
      .reduce((s, [, v]) => s + v.savingCost, 0);
    return fallbackTotal > 0
      ? { labels: ['Unused'], data: [parseFloat(fallbackTotal.toFixed(2))] }
      : { labels: [], data: [] };
  }, [unusedResourcesData, allServicesData]);

  const costByService = useMemo(() => {
    const apiData = costExplorerApiData?.data ?? [];
    const sorted = [...apiData].filter(s => s.totalCostValue > 0).sort((a, b) => b.totalCostValue - a.totalCostValue).slice(0, TOP_SERVICES_LIMIT);
    return { labels: sorted.map(s => s.service), data: sorted.map(s => parseFloat(s.totalCostValue.toFixed(2))) };
  }, [costExplorerApiData]);

  const savingsByRecommendation = useMemo(() => {
    const entries = Object.entries(allServicesData)
      .filter(([key, val]) => !EXCLUDED_FROM_TOTAL_KEYS.includes(key) && (val.savingCost ?? 0) > 0)
      .sort((a, b) => b[1].savingCost - a[1].savingCost);
    return { labels: entries.map(([, v]) => v.serviceName), data: entries.map(([, v]) => parseFloat(v.savingCost.toFixed(2))) };
  }, [allServicesData]);

  const k8sMetrics = useMemo(() => {
    if (!k8sData) return null;
    const clusters = k8sData.clusterDetailsList ?? [];
    const totalCost = k8sData.totalClusterCost ?? 0;
    const idleCost = k8sData.totalIdleClusterCost ?? 0;
    const storageCost = (k8sData.totalClusterEBSCost ?? 0) + (k8sData.totalClusterDiskCost ?? 0);
    const lbCost = clusters.reduce((s, c) => s + (parseFloat(c.clusterLoadBalancerCost) || 0), 0);
    const nodeCost = Math.max(0, totalCost - idleCost - storageCost - lbCost);
    const totalCPU = k8sData.totalClusterCPU ?? 0;
    const usedCPU = k8sData.totalClusterUsedCPU ?? 0;
    const avgUtilization = totalCPU > 0 ? ((usedCPU / totalCPU) * 100).toFixed(1) : null;
    const topClusters = [...clusters].sort((a, b) => (b.clusterTotalCost ?? 0) - (a.clusterTotalCost ?? 0)).slice(0, TOP_CLUSTERS_LIMIT);
    return {
      totalCost, idleCost, storageCost, lbCost, nodeCost,
      activeClusters: k8sData.totalClusters ?? clusters.length,
      totalNodes: k8sData.totalClusterNodes ?? 0,
      avgUtilization,
      topClusters,
      hasData: totalCost > 0 || clusters.length > 0,
    };
  }, [k8sData]);

  const dailyCostTrend = useMemo(() => {
    const apiData = costExplorerApiData?.data ?? [];
    if (!apiData.length) return { labels: [], data: [] };
    const dateMap = {};
    apiData.forEach(svc => {
      Object.entries(svc.costDates ?? {}).forEach(([date, cost]) => {
        dateMap[date] = (dateMap[date] ?? 0) + (parseFloat(cost) || 0);
      });
    });
    const sortedDates = Object.keys(dateMap).sort((a, b) => dayjs(a, 'DD MMM YYYY').valueOf() - dayjs(b, 'DD MMM YYYY').valueOf());
    return { labels: sortedDates, data: sortedDates.map(d => parseFloat(dateMap[d].toFixed(2))) };
  }, [costExplorerApiData]);

  const summary = useMemo(() => [
    { value: totalMonthlyCost },
    { value: ((parseFloat(totalMonthlySaving) || 0) + optimizerMonthlyTotal / 1000).toFixed(2) },
    { value: totalMonthlySavingPer },
  ], [totalMonthlyCost, totalMonthlySaving, totalMonthlySavingPer, optimizerMonthlyTotal]);

  const currentAccountLabel = useMemo(() => {
    const acct = accountList?.find(a => a.accountId === selectedAccount);
    return acct ? `${acct.accountId} — ${acct.accountAliase}` : selectedAccount || '—';
  }, [accountList, selectedAccount]);

  const hasRecs = useMemo(
    () => Object.entries(allServicesData).some(([key, v]) => !EXCLUDED_FROM_TOTAL_KEYS.includes(key) && (v?.savingCost ?? 0) > 0),
    [allServicesData]
  );

  const totalRecSaving = useMemo(
    () => Object.entries(allServicesData)
      .filter(([key]) => !EXCLUDED_FROM_TOTAL_KEYS.includes(key))
      .reduce((s, [, v]) => s + (v?.savingCost ?? 0), 0) + optimizerMonthlyTotal,
    [allServicesData, optimizerMonthlyTotal]
  );

  /* ── API calls ─────────────────────────────────────────────── */
  const fetchData = async () => {
    setIsLoading(true);
    if (selectedPlatform === PLATFORM.AZURE) {
      await DashboardService.fetchAzureData(selectedAccount, selectedDate ?? '');
      setAllServicesData(DashboardService.getAPIData());
      setTotalMonthlyCost(formatValuesInK(DashboardService.getTotalMonthlyCost()));
      const apiData = DashboardService.getAPIData();
      const rawCost = DashboardService.getTotalMonthlyCost();
      const filteredSaving = Object.entries(apiData)
        .filter(([key]) => !EXCLUDED_FROM_TOTAL_KEYS.includes(key))
        .reduce((sum, [, v]) => sum + (v['savingCost'] ?? 0), 0);
      setTotalMonthlySaving(formatValuesInK(filteredSaving));
      setTotalMonthlySavingPer(rawCost > 0 ? ((filteredSaving / rawCost) * 100).toFixed(2) : '0');
      setVMMonthlySaving(formatValuesInK(DashboardService.getVMMonthlySaving()));
      setManagedDiskMonthlySaving(formatValuesInK(DashboardService.getManagedDiskMonthlySaving()));
      setPublicIPMonthlySaving(formatValuesInK(DashboardService.getPublicIPMonthlySaving()));
    } else {
      await DashboardService.fetchData(selectedAccount, selectedDate ?? '');
      setAllServicesData(DashboardService.getAPIData());
      setTotalMonthlyCost(formatValuesInK(DashboardService.getTotalMonthlyCost()));
      setTotalMonthlySaving(formatValuesInK(DashboardService.getTotalMonthlySaving()));
      setTotalMonthlySavingPer(DashboardService.getTotalMonthlySavingPercent());
    }
    setIsLoading(false);
    setLastRefreshed(dayjs());
  };

  const fetchAdvisoryApiData = async () => {
    if (selectedPlatform !== PLATFORM.AZURE) return;
    await DashboardService.fetchAzureAdvisoryData(selectedAccount, selectedDate ?? '');
    const advisoryData = DashboardService.getAdvisoryAPIData();
    if (advisoryData?.length > 0) {
      const allKeysSet = new Set();
      advisoryData.forEach(row => Object.keys(row).forEach(key => {
        if (key !== 'recommendationOfferingId' && key !== 'id') allKeysSet.add(key);
      }));
      const allKeys = Array.from(allKeysSet);
      setAdvisoryColumnList([
        { key: 'recommendationOfferingId', label: 'Rec. Offering ID' },
        { key: 'id', label: 'Instance ID' },
        ...allKeys.map(key => ({
          key,
          label: key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()),
        })),
      ]);
      setAdvisoryApiData(advisoryData);
    }
  };

  const fetchComputeOptimizerApiData = async () => {
    if (selectedPlatform !== PLATFORM.AWS) { setIsOptimizerLoading(false); return; }
    setIsOptimizerLoading(true);
    try {
      await DashboardService.fetchComputeOptimizerData(selectedAccount, selectedDate ?? '');
      const resp = DashboardService.getComputeOptimizerAPIData() ?? [];
      const rawData = Array.isArray(resp) ? resp : (resp?.data ?? []);
      setAllOptimizerColumnList(OPTIMIZER_COLUMNS);
      setAllOptimizerData(rawData);
      setUnusedResourcesData(rawData);
    } catch (err) {
      DebugService.error('Dashboard: optimizer fetch failed', err);
    } finally {
      setIsOptimizerLoading(false);
    }
  };

  const fetchCostAnomalyData = async () => {
    if (selectedPlatform !== PLATFORM.AWS) return;
    try {
      await DashboardService.fetchCostAnomalyData(selectedAccount, selectedDate ?? '');
      const rawData = DashboardService.getCostAnomalyAPIData() ?? [];
      const report = rawData?.[0]?.costIncreaseReport ?? [];
      const sevColor = { High: '#e53935', Med: '#fb8c00', Low: '#43a047' };
      setAnomalyColumnList([
        { key: 'severity', label: 'Severity', render: (row) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: sevColor[row.severity] ?? '#999', flexShrink: 0 }} />
            {row.severity}
          </span>
        )},
        { key: 'date',             label: 'Date' },
        { key: 'service',          label: 'Service' },
        { key: 'region',           label: 'Region' },
        { key: 'currentCost',      label: 'Current ($)' },
        { key: 'previousCost',     label: 'Previous ($)' },
        { key: 'increaseCost',     label: 'Increase ($)' },
        { key: 'percentageChange', label: 'Change', render: (row) => {
          const pct = parseFloat(row.percentageChange) || 0;
          return <span className={`delta-pill ${pct >= 0 ? 'up' : 'down'}`}>{pct >= 0 ? '↑' : '↓'} {Math.abs(pct)}%</span>;
        }},
      ]);
      setAnomalyData(report.map((item, i) => ({
        id: item.id ?? i, severity: item.severity, date: item.date, service: item.service,
        region: item.region, currentCost: item.currentCost, previousCost: item.previousCost,
        increaseCost: item.increaseCost, percentageChange: item.percentageChange,
      })));
    } catch (err) {
      DebugService.error('Dashboard: anomaly fetch failed', err);
    }
  };

  const fetchK8sData = async () => {
    setIsK8sLoading(true);
    try {
      const platform = selectedPlatform === PLATFORM.AZURE ? 'aks' : 'eks';
      const resp = await K8sApiService.getClusterData(platform, {
        userAccountId: selectedAccount ?? '',
        // fromDate: dataFilters.startMonthDate?.format('YYYY-MM-DD'),
        // toDate: dataFilters.endMonthDate?.format('YYYY-MM-DD'),
      });
      DebugService.log('[Dashboard] K8s cluster data:', resp);
      setK8sData(resp?.data ?? null);
      setLastK8sRefreshed(dayjs());
    } catch (err) {
      DebugService.error('[Dashboard] K8s fetch failed', err);
    } finally {
      setIsK8sLoading(false);
    }
  };

  const fetchCostExplorerData = () => {
    setCostExplorerData([]);
    setCostExplorerColumnList([]);
    const req = {
      ...dataFilters,
      userAccountId: selectedAccount,
      type: 'COST_EXPLORER',
      startMonthDate: dataFilters.startMonthDate?.format('YYYY-MM-DD'),
      endMonthDate: dataFilters.endMonthDate?.format('YYYY-MM-DD'),
    };
    delete req['filter'];

    const call = selectedPlatform === PLATFORM.AZURE
      ? BillingApiService.getAzureCostExplorerData(req)
      : selectedPlatform === PLATFORM.AWS
        ? BillingApiService.getCostExplorerData(req)
        : null;

    if (!call) return Promise.resolve();
    return call.then(res => {
      setCostExplorerApiData(res);
      const apiData = res?.data ?? [];
      if (apiData.length > 0 && apiData[0]['costDates']) {
        const keys = [];
        let cur = dataFilters.startMonthDate.startOf('day');
        const last = dataFilters.endMonthDate.startOf('day');
        while (cur.isBefore(last) || cur.isSame(last, 'day')) {
          keys.push(cur.format('YYYY-MM-DD'));
          cur = cur.add(1, 'day');
        }
        if (keys.length > 0) {
          const totalCellStyle = { fontWeight: 700, color: 'var(--brand, #2196f3)' };
          setCostExplorerColumnList([
            {
              key: 'service',
              label: toWholeString(dataFilters.dimension ?? 'Service'),
              render: (row) => row.__isTotal
                ? <span style={{ fontWeight: 700 }}>Total (all services)</span>
                : (row.service || row.SERVICE || row.serviceName || row.id || '—'),
            },
            {
              key: 'totalCostValue',
              label: 'Total Cost',
              render: (row) => {
                const text = `$${parseFloat(row.totalCostValue ?? 0).toFixed(2)}`;
                return row.__isTotal ? <span style={totalCellStyle}>{text}</span> : text;
              },
            },
            ...keys.map(d => ({
              key: d,
              label: dayjs(d).format('DD MMM YYYY'),
              render: (row) => {
                const v = row.costDates?.[dayjs(d).format('DD MMM YYYY')];
                const text = v != null ? `$${parseFloat(v).toFixed(2)}` : '$0.00';
                return row.__isTotal ? <span style={totalCellStyle}>{text}</span> : text;
              },
            })),
          ]);
        }
        const sortedRows = apiData
          .sort((a, b) => b.totalCostValue - a.totalCostValue)
          .map(s => ({ ...s, id: s.service ?? s.SERVICE ?? s.serviceName }));
        // Grand-total row summing every service (overall total + per-day totals)
        const totalRow = {
          id: '__total__',
          __isTotal: true,
          totalCostValue: sortedRows.reduce((sum, r) => sum + (parseFloat(r.totalCostValue) || 0), 0),
          costDates: keys.reduce((acc, d) => {
            const label = dayjs(d).format('DD MMM YYYY');
            acc[label] = sortedRows.reduce((sum, r) => sum + (parseFloat(r.costDates?.[label]) || 0), 0);
            return acc;
          }, {}),
        };
        setCostExplorerData(sortedRows.length > 0 ? [totalRow, ...sortedRows] : sortedRows);
      }
    }).catch(err => DebugService.error('Dashboard: cost explorer fetch failed', err));
  };

  useEffect(() => {
    document.title = `${CONST_SITE_LABEL} | Dashboard`;
  }, []);

  useEffect(() => {
    const daysBefore = selectedDate ? selectedDate.subtract(30, 'days') : dayjs().subtract(30, 'days');
    setDataFilters({
      startMonthDate: daysBefore,
      endMonthDate: selectedDate ?? dayjs(),
      granularity: 'DAILY',
      dimension: selectedPlatform === PLATFORM.AWS ? 'SERVICE' : 'SERVICE_NAME',
      filter: '',
    });
    if (selectedPlatform !== PLATFORM.AZURE) {
      setVMMonthlySaving('0');
      setManagedDiskMonthlySaving('0');
      setPublicIPMonthlySaving('0');
    }
  }, [selectedDate, selectedAccount, selectedPlatform]);

  useEffect(() => {
    // Skip until AuthContext has resolved a real account (ADD_NEW_ACCOUNT is the placeholder initial value).
    if (!selectedAccount || selectedAccount === ADD_NEW_ACCOUNT) return;
    // Wait for fetchUserData() to fully complete — it saves userDetails to localStorage,
    // which RecommendationApiService needs to build the userId in the request body.
    // Without this guard, the first login fires recommendations before userId is available → 502.
    if (loadingUserDetails !== false) return;
    // Deduplicate: skip if account/platform/date haven't changed — guards against
    // StrictMode double-mount and back-to-back renders with identical params.
    const fetchParams = `${selectedAccount}_${selectedPlatform}_${dataFilters.startMonthDate?.format('YYYY-MM-DD')}`;
    if (lastFetchParamsRef.current === fetchParams) return;
    lastFetchParamsRef.current = fetchParams;
    setIsPageLoading(true);
    Promise.all([
      fetchData(),
      fetchCostExplorerData() ?? Promise.resolve(),
      fetchAdvisoryApiData(),
      fetchComputeOptimizerApiData(),
      fetchCostAnomalyData(),
      fetchK8sData(),
    ]).finally(() => setIsPageLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataFilters.startMonthDate, loadingUserDetails]);

  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      allServicesData, advisoryApiData, costExplorerApiData, computeOptimizerApiData: allOptimizerData,
    });
  }, [allServicesData, advisoryApiData, costExplorerApiData, allOptimizerData]);

  const navigateToOptimization = useCallback((key) => {
    const path = {
      VM: CONST_ROUTE_PATH.OPTIMIZATION.VIRTUAL_MACHINE,
      MANAGED_DISK: CONST_ROUTE_PATH.OPTIMIZATION.MANAGED_DISKS,
      EKS: CONST_ROUTE_PATH.K8S.CONNECT_K8S_CLUSTER,
      POSTGRESQL: CONST_ROUTE_PATH.OPTIMIZATION.AZURE_DB_POSTGRESSQL,
    }[key] ?? CONST_ROUTE_PATH.OPTIMIZATION[key];
    if (path) { setScopedItem(STORAGE_KEYS.CURRENT_ROUTE, path); navigate(path); }
  }, []);

  /* ── Full-page shimmer while all APIs are in-flight ────────── */
  if (isPageLoading) {
    return (
      <>
        {/* Header */}
        <div className="page-head" style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="shimmer-bg" style={{ width: 110, height: 22, borderRadius: 5 }} />
              <div className="shimmer-bg shimmer-badge" />
            </div>
            <div className="shimmer-bg" style={{ width: 260, height: 12, borderRadius: 4 }} />
          </div>
          <div className="shimmer-bg" style={{ width: 220, height: 42, borderRadius: 9 }} />
        </div>

        {/* KPI tiles */}
        <KpiSkeleton count={3} cols={3} />

        {/* Service cards */}
        <SvcGridSkeleton count={5} />

        {/* 3 charts */}
        <ChartGridSkeleton cols={3} />

        {/* Daily cost trend */}
        <div style={{ background: '#fff', border: '1px solid #e9ecf2', borderRadius: 11, padding: '16px 18px', marginBottom: 14 }}>
          <div className="shimmer-bg" style={{ width: 180, height: 13, marginBottom: 6 }} />
          <div className="shimmer-bg" style={{ width: 120, height: 10, marginBottom: 18 }} />
          <div className="shimmer-bg" style={{ width: '100%', height: 140, borderRadius: 8 }} />
        </div>

        {/* Optimizer table */}
        <TableSkeleton rows={5} cols={6} />

        {/* Bottom grid: cost breakup + recommendations */}
        <div className="grid-bottom">
          <TableSkeleton rows={6} cols={4} />
          <RecsPanelSkeleton />
        </div>

        {/* Cost anomaly table */}
        <TableSkeleton rows={4} cols={5} />
      </>
    );
  }

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <>
      <div className="page-head">
        <div>
          <h1>
            Dashboard{' '}
            {isLoading ? <BadgeSkeleton /> : (
              <span className="scope-badge">
                {accountList?.length > 1 && !selectedAccount ? 'ALL ACCOUNTS' : 'SINGLE ACCOUNT'}
              </span>
            )}
          </h1>
          <div className="meta">
            Viewing <b>{currentAccountLabel}</b>
            {lastRefreshed && <> · last refreshed {lastRefreshed.fromNow()}</>}
          </div>
        </div>
        <div className="head-actions">
          <button className="btn" onClick={() => navigate(CONST_ROUTE_PATH.ADD_ACCOUNT)}>＋ Add Account</button>

          {/* Date range pill — click opens the MUI DatePicker anchored here */}
          <div
            ref={datePillRef}
            className="date-pill"
            onClick={() => setDatePickerOpen(true)}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <small style={{ color: 'var(--muted)' }}>📅 Check History By Date</small>
            {selectedDate ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <strong>
                  {dataFilters.startMonthDate?.format('DD MMM YYYY')} – {dataFilters.endMonthDate?.format('DD MMM YYYY')}
                </strong>
                <span
                  title="Clear date"
                  onClick={e => { e.stopPropagation(); setSelectedDate(null); }}
                  style={{ fontSize: 13, color: 'var(--muted)', cursor: 'pointer', lineHeight: 1 }}
                >✕</span>
              </span>
            ) : (
              <span style={{ color: 'var(--muted)', fontSize: 13, fontStyle: 'italic' }}>Select a date…</span>
            )}
            <span style={{ color: 'var(--muted)' }}>▾</span>
          </div>

          {/* DatePicker anchored to the pill above — text field hidden, calendar opens in place */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              value={selectedDate}
              onChange={v => { setSelectedDate(v); setDatePickerOpen(false); }}
              open={datePickerOpen}
              onClose={() => setDatePickerOpen(false)}
              disableFuture
              slotProps={{
                textField: { sx: { display: 'none' } },
                popper: {
                  anchorEl: pillMounted ? datePillRef.current : null,
                  placement: 'bottom-end',
                  modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
                },
              }}
            />
          </LocalizationProvider>
        </div>
      </div>

      {/* ── KPI Tiles ───────────────────────────────────────────── */}
      <div className="kpi-row cols-3">
        <div className="kpi t4">
          <div className="ico"><FaDollarSign /></div>
          <div>
            <div className="lbl">Forecast Cost</div>
            <div className="val">${summary[0].value}K</div>
            <div className="sub">Annual (×{MONTHS_PER_YEAR}) · ${(parseFloat(summary[0].value) * MONTHS_PER_YEAR).toFixed(2)}K</div>
          </div>
        </div>
        <div className="kpi t1">
          <div className="ico"><FaPiggyBank /></div>
          <div>
            <div className="lbl">Forecast Possible Saving</div>
            <div className="val">${summary[1].value}K</div>
            <div className="sub">Annual (×{MONTHS_PER_YEAR}) · ${(parseFloat(summary[1].value) * MONTHS_PER_YEAR).toFixed(2)}K</div>
          </div>
        </div>
        <div className="kpi t2">
          <div className="ico"><FaChartBar /></div>
          <div>
            <div className="lbl">% of Forecast Possible Saving</div>
            <div className="val">{summary[2].value}%</div>
            <div className="sub">Across all services</div>
          </div>
        </div>
      </div>

      {/* ── Service Recommendation Cards ─────────────────────────── */}
      {selectedPlatform === PLATFORM.AWS && (
        <div className="svc-grid" style={{ gridTemplateColumns: `repeat(${AWS_SVC_CARDS.length}, 1fr)` }}>
          {AWS_SVC_CARDS.map(({ key, label, costKey }) => {
            const d      = allServicesData[key] || {};
            const cost   = d[costKey] || 0;
            const saving = d.savingCost || 0;
            return (
              <div key={key} className="svc-card">
                <div className="svc-head">{label}</div>
                <div className="svc-body">
                  <div className="svc-title-row">
                    <span className="svc-label">Forecast Possible Savings</span>
                    <span className={`pill${saving === 0 ? ' warn' : ''}`}>${formatValuesInK(saving)}K</span>
                  </div>
                  <div className="chart-sm">
                    <ServiceMiniBarChart cost={cost} saving={saving} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedPlatform === PLATFORM.AZURE && (
        <div className="svc-grid" style={{ gridTemplateColumns: `repeat(${AZURE_SVC_CARDS.length}, 1fr)` }}>
          {AZURE_SVC_CARDS.map(({ key, label, costKey }) => {
            const d      = allServicesData[key] || {};
            const cost   = d[costKey] || 0;
            const saving = d.savingCost || 0;
            return (
              <div key={key} className="svc-card">
                <div className="svc-head">{label}</div>
                <div className="svc-body">
                  <div className="svc-title-row">
                    <span className="svc-label">Forecast Possible Savings</span>
                    <span className={`pill${saving === 0 ? ' warn' : ''}`}>${formatValuesInK(saving)}K</span>
                  </div>
                  <div className="chart-sm">
                    <ServiceMiniBarChart cost={cost} saving={saving} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Three Charts (AWS only) ──────────────────────────────── */}
      {selectedPlatform === PLATFORM.AWS && (
        savingsByResourceType.labels.length > 0 ||
        costByService.labels.length > 0 ||
        savingsByRecommendation.labels.length > 0
      ) && (
        <div className="dash-grid-3">
          {savingsByResourceType.labels.length > 0 && (
            <HorizontalBarChart
              labels={savingsByResourceType.labels}
              data={savingsByResourceType.data}
              color={savingsByResourceType.labels.map(l => l === 'Idle' ? CHART_COLORS.idle : CHART_COLORS.unused)}
              title="Unused Resources And Idle Resources"
              subTitle="Savings from unused and idle resources"
            />
          )}
          {costByService.labels.length > 0 && (
            <HorizontalBarChart
              labels={costByService.labels.map(l => shortenLabel(l))}
              fullLabels={costByService.labels}
              data={costByService.data}
              color={CHART_COLORS.savings}
              title="Cost by Service"
              subTitle={`Top ${TOP_SERVICES_LIMIT} services by total cost`}
            />
          )}
          {savingsByRecommendation.labels.length > 0 && (
            <HorizontalBarChart
              labels={savingsByRecommendation.labels}
              data={savingsByRecommendation.data}
              color={CHART_COLORS.unused}
              title="Service wise saving Recommendation"
              subTitle="Potential savings across all services"
            />
          )}
        </div>
      )}
      
      {/* ── Daily Cost Trend (AWS) ──────────────────────────────── */}
      {selectedPlatform === PLATFORM.AWS && dailyCostTrend.labels.length > 0 && (
        <DailyCostTrendChart labels={dailyCostTrend.labels} data={dailyCostTrend.data} platformLabel="AWS" />
      )}
      
      {/* ── Optimizer Recommendations (AWS) ─────────────────────── */}
      {selectedPlatform === PLATFORM.AWS && (
        <Table
          rows={allOptimizerData}
          columns={allOptimizerColumnList}
          loading={isOptimizerLoading}
          thClassName="svc-head"
          defaultPageSize={10}
          cardClassName="card first"
          headerExtra={<h3 style={{ margin: 0 }}>Optimizer Recommendations</h3>}
          onExport={(rows) => exportCSV(rows, allOptimizerColumnList.filter((c) => typeof c.label === 'string'), buildExportFilename('optimizer_recommendations', selectedAccount))}
          emptyText="No optimizer recommendations found."
          subscriptionGated
        />
      )}

     

      {/* ── Service-wise Cost Breakup + Recommendations ─────────── */}
      <div className="grid-bottom">
        <div style={{ minWidth: 0, overflow: 'hidden' }}>
          <Table
            rows={costExplorerData}
            columns={costExplorerColumnList}
            loading={isLoading}
            thClassName="svc-head"
            defaultPageSize={10}
            cardClassName="card first"
            headerExtra={<h3 style={{ margin: 0 }}>Service-wise Cost Breakup Trend</h3>}
            onExport={(rows) => exportCSV(rows, costExplorerColumnList.filter((c) => typeof c.label === 'string'), buildExportFilename('service_cost_breakup_trend', selectedAccount))}
            emptyText="No cost data found."
            subscriptionGated
          />
        </div>

        {/* Recommendations Panel */}
        <Card
          title="Recommendations"
          className="first"
        >
          {hasRecs ? (
            <div className="rec-scroll">
              {Object.entries(allServicesData)
                .filter(([key, v]) => !EXCLUDED_FROM_TOTAL_KEYS.includes(key) && (v?.savingCost ?? 0) > 0)
                .map(([key, v]) => {
                  const tagClass = SVC_TAG_CLASS[key] || '';
                  const isOptimizer = key === 'COMPUTE_OPTIMIZER';
                  const names = (v.serviceName ?? '').split(',').map(s => s.trim()).filter(Boolean);
                  return (
                    <div key={key} className="rec-item">
                      <div className="rec-item-top">
                        <span className="rec-item-title">{v.displasyAs ?? v.displayAs ?? v.serviceName ?? key}</span>
                        <span className="rec-item-amt">
                          ${(v.savingCost ?? 0).toFixed(2)}
                          {!isOptimizer && (
                            <OpenInNewIcon
                              onClick={() => navigateToOptimization(key)}
                              sx={{ fontSize: 13, ml: 0.5, color: '#2196f3', verticalAlign: 'middle', cursor: 'pointer' }}
                            />
                          )}
                        </span>
                      </div>
                      {names.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                          {names.map((name, ni) => (
                            <span key={ni} className={`tag ${tagClass}`}>{name}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="panel-empty">
              <div className="panel-empty-ico">✅</div>
              <div className="panel-empty-txt">No recommendations at this time</div>
            </div>
          )}
        </Card>
      </div>

      {/* ── Cost Anomaly (AWS) ──────────────────────────────────── */}
      {selectedPlatform === PLATFORM.AWS && (
        <Table
          rows={anomalyData}
          columns={anomalyColumnList}
          loading={isLoading}
          thClassName="svc-head"
          defaultPageSize={10}
          cardClassName="card first"
          headerExtra={
            <h3 style={{ margin: 0 }}>
              Service wise Cost Anomaly
              {anomalyData.length > 0 && (
                <span className="anom-badge" style={{ marginLeft: 8 }}>⚠ {anomalyData.length} detected</span>
              )}
            </h3>
          }
          onExport={(rows) => exportCSV(rows, anomalyColumnList.filter((c) => typeof c.label === 'string'), buildExportFilename('service_cost_anomaly', selectedAccount))}
          emptyText="No cost anomalies detected."
          subscriptionGated
        />
      )}

      {/* ── Kubernetes at a glance ──────────────────────────────── */}
      {(isK8sLoading || k8sMetrics?.hasData) && (
      <div style={{ marginTop: 28, paddingTop: 22, borderTop: '1px dashed #e9ecf2' }}>

          {/* Section header */}
          <div className="section-h">
            <div className="ico-circle k8s">⎈</div>
            <h2>Kubernetes — at a glance</h2>
            <span className="s-meta">
              Powered by OpenCost + Prometheus
              {lastK8sRefreshed && <> · refreshed {lastK8sRefreshed.fromNow()}</>} ·&nbsp;
              <button
                className="btn-link"
                onClick={() => navigate(CONST_ROUTE_PATH.K8S.CONNECT_K8S_CLUSTER)}
              >
                Open full K8s dashboard →
              </button>
            </span>
          </div>

          {/* Loading state — show while fetching, hide once data arrives */}
          {isK8sLoading && !k8sMetrics && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 80, color: 'var(--muted)', gap: 10 }}>
              <span className="k8s-spinner" />
              Loading Kubernetes data…
            </div>
          )}

          {/* 4 KPI tiles — only render once we have real data */}
          {k8sMetrics && (
          <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="kpi t2">
              <div className="ico"><FaDollarSign /></div>
              <div>
                <div className="lbl">K8s Total Cost</div>
                <div className="val">${k8sMetrics.totalCost.toFixed(2)}</div>
                <div className="sub">Across {k8sMetrics.activeClusters} clusters</div>
              </div>
            </div>
            <div className="kpi t1">
              <div className="ico"><FaCloud /></div>
              <div>
                <div className="lbl">Active Clusters</div>
                <div className="val">{k8sMetrics.activeClusters}</div>
                <div className="sub">{k8sMetrics.totalNodes} nodes total</div>
              </div>
            </div>
            <div className="kpi t6">
              <div className="ico"><FaExclamationTriangle /></div>
              <div>
                <div className="lbl">Idle Cost</div>
                <div className="val">${k8sMetrics.idleCost.toFixed(2)}</div>
                <div className="sub">
                  {k8sMetrics.totalCost > 0
                    ? `${((k8sMetrics.idleCost / k8sMetrics.totalCost) * 100).toFixed(1)}% of total`
                    : '—'}
                </div>
              </div>
            </div>
            <div className="kpi t3">
              <div className="ico"><FaCogs /></div>
              <div>
                <div className="lbl">Avg Utilization</div>
                <div className="val">{k8sMetrics.avgUtilization != null ? `${k8sMetrics.avgUtilization}%` : '—'}</div>
                <div className="sub">CPU · Mem across all nodes</div>
              </div>
            </div>
          </div>
          )}

          {/* Cost Allocation & Cluster Snapshot — only render once we have real data */}
          {k8sMetrics && (
          <Card title="Cost Allocation & Cluster Snapshot" subtitle="Cost split by resource type · top clusters by spend" className="first">
            <div className="k8s-alloc-grid">

              {/* Donut */}
              <div style={{ position: 'relative', width: 180, height: 180, flexShrink: 0 }}>
                <K8sCostDonutChart
                  nodeCost={k8sMetrics.nodeCost}
                  storageCost={k8sMetrics.storageCost}
                  lbCost={k8sMetrics.lbCost}
                  idleCost={k8sMetrics.idleCost}
                />
                <div className="k8s-donut-center">
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>Total</span>
                  <strong style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.5px' }}>
                    ${k8sMetrics.totalCost.toFixed(2)}
                  </strong>
                </div>
              </div>

              {/* Legend */}
              <div className="k8s-legend">
                {[
                  { color: CHART_COLORS.node,    label: 'Node',          val: k8sMetrics.nodeCost },
                  { color: CHART_COLORS.storage,  label: 'Storage',       val: k8sMetrics.storageCost },
                  { color: CHART_COLORS.lb,       label: 'Load Balancer', val: k8sMetrics.lbCost },
                  { color: CHART_COLORS.idle,     label: 'Idle',          val: k8sMetrics.idleCost },
                ].map(({ color, label, val }) => (
                  <div key={label} className="k8s-legend-row">
                    <span className="k8s-legend-dot" style={{ background: color }} />
                    <span className="k8s-legend-lbl">{label}</span>
                    <span className="k8s-legend-val">${(val ?? 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              {/* Top clusters */}
              <div className="k8s-clusters">
                <div className="k8s-clusters-head">Top Clusters</div>
                {(k8sMetrics?.topClusters ?? []).length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--muted)', padding: '8px 0' }}>No cluster data available</div>
                )}
                {(k8sMetrics?.topClusters ?? []).map((c, i) => {
                  const idleAmt = parseFloat(c.clusterIdleCost) || 0;
                  return (
                    <div key={i} className="k8s-cluster-row">
                      <span className="k8s-cluster-ico">☁</span>
                      <div className="k8s-cluster-info">
                        <b>{c.clusterName}</b>
                        <small>
                          {c.region}
                          {c.clusterEfficiency != null ? ` · ${c.clusterEfficiency}% CPU` : ''}
                          {idleAmt > 0 && (
                            <> · <span style={{ color: 'var(--red)' }}>${idleAmt.toFixed(2)} idle</span></>
                          )}
                        </small>
                      </div>
                      <b className="k8s-cluster-cost">${(c.clusterTotalCost ?? 0).toFixed(2)}</b>
                    </div>
                  );
                })}
                <button
                  className="btn"
                  style={{ marginTop: 14, width: '100%', justifyContent: 'center', fontSize: '11.5px' }}
                  onClick={() => navigate(CONST_ROUTE_PATH.K8S.CONNECT_K8S_CLUSTER)}
                >
                  View all clusters →
                </button>
              </div>

            </div>
          </Card>
          )}
        </div>
      )}
    </>
  );
};

export default Dashboard;
